// src/common/hooks/useSyncPendingActions.ts
import { useEffect, useRef, useCallback, useState } from "react";
import useInterval from "@use-it/interval";
import { store } from "../../store/store";
import { getApi } from "../../api/api";
import {
  finishSync,
  removePendingAction,
  startSync,
  SyncActionType,
  SyncStatus,
} from "../../store/slices/syncSlice";
import { setFavorites } from "../../store/slices/favoritesSlice";

// ========== КОНСТАНТЫ ==========
const SYNC_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 30 * 1000,
  CLEANUP_INTERVAL: 24 * 60 * 60 * 1000,
  SYNC_INTERVAL: 15 * 60 * 1000,
  VIEWS_BATCH_THRESHOLD: 3,
} as const;

// ========== ХУК ==========
export const useSyncPendingActions = () => {
  const retryTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const [api] = useState(getApi);

  // === ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ===
  const getErrorString = useCallback((error: unknown): string => {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    return "Неизвестная ошибка";
  }, []);

  // === ПРОВЕРКА СОЕДИНЕНИЯ ===
  const checkOnline = useCallback((): boolean => {
    return navigator.onLine;
  }, []);

  // === ОСНОВНАЯ СИНХРОНИЗАЦИЯ ===
  const performSync = useCallback(async () => {
    const state = store.getState().sync;
    const { pendingActions, isSyncing } = state;

    if (isSyncing) return;
    if (pendingActions.length === 0) return;
    if (!checkOnline()) return;

    try {
      store.dispatch(startSync());

      const ratings = pendingActions.filter(
        (a) => a.type === SyncActionType.RATING,
      );
      const favorites = pendingActions.filter(
        (a) => a.type === SyncActionType.FAVORITE,
      );
      const views = pendingActions.filter(
        (a) => a.type === SyncActionType.VIEW,
      );
      const serviceOtps = pendingActions.filter(
        (a) => a.type === SyncActionType.SERVICE_OTP,
      );

      if (
        ratings.length === 0 &&
        favorites.length === 0 &&
        views.length === 0 &&
        serviceOtps.length === 0
      ) {
        store.dispatch(
          finishSync({ success: false, error: "Unknown action types" }),
        );
        return;
      }

      const errors: string[] = [];

      // ОЦЕНКИ
      if (ratings.length > 0) {
        for (const rating of ratings) {
          try {
            await api.rateItem(rating.itemId, {
              rating: rating.data!.rating!,
              comment: rating.data?.comment,
            });
            store.dispatch(removePendingAction(rating.localId));
          } catch (error) {
            const errorMsg = getErrorString(error);
            errors.push(`Оценка ${rating.itemId}: ${errorMsg}`);
          }
        }
      }

      // ИЗБРАННОЕ
      if (favorites.length > 0) {
        for (const favorite of favorites) {
          try {
            await api.toggleFavorite(favorite.itemId);
            store.dispatch(removePendingAction(favorite.localId));
          } catch (error) {
            const errorMsg = getErrorString(error);
            errors.push(`Избранное ${favorite.itemId}: ${errorMsg}`);
          }
        }
        // Перезагружаем избранное после синхронизации
        try {
          const response = await api.getUserFavorites({ limit: 1000 });
          if (response) {
            store.dispatch(setFavorites(response));
          }
        } catch (error) {
          console.error("Failed to refresh favorites after sync:", error);
        }
      }

      // ПРОСМОТРЫ
      if (views.length > 0) {
        const viewIds = views.map((v) => v.itemId);
        try {
          if (views.length >= SYNC_CONFIG.VIEWS_BATCH_THRESHOLD) {
            await api.trackViewsBatch(viewIds);
          } else {
            for (const viewId of viewIds) {
              await api.trackView(viewId);
            }
          }
          views.forEach((view) => {
            store.dispatch(removePendingAction(view.localId));
          });
        } catch (error) {
          const errorMsg = getErrorString(error);
          errors.push(`Просмотры: ${errorMsg}`);
        }
      }

      // SERVICE_OTP
      if (serviceOtps.length > 0) {
        for (const otp of serviceOtps) {
          try {
            await api.useServiceOtp(otp.data!.code!);
            store.dispatch(removePendingAction(otp.localId));
          } catch (error) {
            const errorMsg = getErrorString(error);
            errors.push(`OTP ${otp.data!.code!}: ${errorMsg}`);
          }
        }
      }

      if (errors.length === 0) {
        store.dispatch(finishSync({ success: true }));
      } else {
        store.dispatch(
          finishSync({ success: false, error: errors.join("; ") }),
        );
      }
    } catch (error) {
      const errorMsg = getErrorString(error);
      store.dispatch(finishSync({ success: false, error: errorMsg }));
    }
  }, [api, getErrorString]);

  // === ОЧИСТКА ===
  const performCleanup = useCallback(() => {
    const state = store.getState().sync;
    const now = Date.now();

    const oldSynced = state.pendingActions.filter(
      (a) =>
        a.status === SyncStatus.SYNCED &&
        now - a.timestamp > SYNC_CONFIG.CLEANUP_INTERVAL,
    );

    const failedToRemove = state.pendingActions.filter(
      (a) =>
        a.status === SyncStatus.FAILED &&
        a.retryCount > SYNC_CONFIG.MAX_RETRIES,
    );

    if (oldSynced.length > 0 || failedToRemove.length > 0) {
      oldSynced.forEach((a) => store.dispatch(removePendingAction(a.localId)));
      failedToRemove.forEach((a) =>
        store.dispatch(removePendingAction(a.localId)),
      );
    }
  }, []);

  // === ОБРАБОТЧИК ONLINE/OFFLINE ===
  const handleOnline = useCallback(() => {
    performSync();
  }, [performSync]);

  const handleOffline = useCallback(() => {
    // Можно добавить уведомление при желании
  }, []);

  // === ИНИЦИАЛИЗАЦИЯ ===
  useEffect(() => {
    const initialTimer = setTimeout(() => {
      performSync();
    }, 3000);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      clearTimeout(initialTimer);
      retryTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      retryTimeoutsRef.current.clear();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [performSync, handleOnline, handleOffline]);

  // === ИНТЕРВАЛЫ ===
  useInterval(performSync, SYNC_CONFIG.SYNC_INTERVAL);
  useInterval(performCleanup, SYNC_CONFIG.CLEANUP_INTERVAL);

  // === РУЧНАЯ СИНХРОНИЗАЦИЯ ===
  const syncNow = useCallback(async () => {
    performSync();
  }, [performSync]);

  // === СТАТУС ===
  const getSyncStatus = useCallback(() => {
    const state = store.getState().sync;
    return {
      pendingCount: state.pendingActions.length,
      isSyncing: state.isSyncing,
      lastSyncTime: state.lastSyncTime,
      error: state.error,
      stats: {
        favorites: state.pendingActions.filter(
          (a) => a.type === SyncActionType.FAVORITE,
        ).length,
        ratings: state.pendingActions.filter(
          (a) => a.type === SyncActionType.RATING,
        ).length,
        views: state.pendingActions.filter(
          (a) => a.type === SyncActionType.VIEW,
        ).length,
        serviceOtps: state.pendingActions.filter(
          (a) => a.type === SyncActionType.SERVICE_OTP,
        ).length,
        failed: state.pendingActions.filter(
          (a) => a.status === SyncStatus.FAILED,
        ).length,
      },
    };
  }, []);

  return {
    syncNow,
    getSyncStatus,
  };
};
